import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API } from "@/lib/scroll";
import { useLang } from "@/i18n";

const LangToggleMini = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="admin-lang-toggle" className="flex items-center border border-white/20 text-[11px] font-bold tracking-[0.15em]">
      {["id", "en"].map((l) => (
        <button
          key={l}
          data-testid={`admin-lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 uppercase transition-colors duration-300 cursor-pointer ${
            lang === l ? "bg-[#D4AF37] text-black" : "text-neutral-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Admin = () => {
  const { t } = useLang();
  const a = t.admin;
  const [enquiries, setEnquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [enq, subs] = await Promise.all([
        axios.get(`${API}/enquiries`),
        axios.get(`${API}/subscribers`),
      ]);
      setEnquiries(enq.data);
      setSubscribers(subs.data);
    } catch (e) {
      setError(a.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (d) => new Date(d).toLocaleString(a.locale, { dateStyle: "medium", timeStyle: "short" });

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#050505] text-white py-16">
      <div className="container-x">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/" data-testid="admin-back-link" className="text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-[#D4AF37] font-bold transition-colors duration-300">
              {a.back}
            </Link>
            <h1 className="font-display text-5xl md:text-6xl uppercase mt-3">
              FitBersama <span className="text-[#D91624]">{a.titleB}</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <LangToggleMini />
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="btn-outline !px-6 !py-3"
            >
              <RefreshCw size={14} /> {a.refresh}
            </button>
          </div>
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-6 text-[#D91624] text-sm font-bold">
            {error}
          </p>
        )}

        <section className="mt-12">
          <h2 className="font-display text-3xl uppercase tracking-wide">
            {a.enquiries} <span className="text-[#D4AF37]">({enquiries.length})</span>
          </h2>
          <div data-testid="admin-enquiries-table" className="mt-5 border border-white/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.date}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.name}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.email}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.topic}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.message}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.length === 0 && !loading && (
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableCell colSpan={5} className="text-neutral-600 text-center py-10">
                      {a.emptyEnquiries}
                    </TableCell>
                  </TableRow>
                )}
                {enquiries.map((e) => (
                  <TableRow key={e.id} data-testid={`admin-enquiry-${e.id}`} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-neutral-400 text-xs whitespace-nowrap">{fmt(e.created_at)}</TableCell>
                    <TableCell className="text-white font-semibold">{e.name}</TableCell>
                    <TableCell className="text-neutral-300">{e.email}</TableCell>
                    <TableCell className="text-[#D4AF37] text-xs font-bold whitespace-nowrap">{e.topic}</TableCell>
                    <TableCell className="text-neutral-400 text-sm max-w-md">{e.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-3xl uppercase tracking-wide">
            {a.subscribers} <span className="text-[#D4AF37]">({subscribers.length})</span>
          </h2>
          <div data-testid="admin-subscribers-table" className="mt-5 border border-white/10 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.date}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.email}</TableHead>
                  <TableHead className="text-neutral-500 uppercase text-xs tracking-[0.15em]">{a.cols.source}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.length === 0 && !loading && (
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableCell colSpan={3} className="text-neutral-600 text-center py-10">
                      {a.emptySubscribers}
                    </TableCell>
                  </TableRow>
                )}
                {subscribers.map((s) => (
                  <TableRow key={s.id} data-testid={`admin-subscriber-${s.id}`} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-neutral-400 text-xs whitespace-nowrap">{fmt(s.created_at)}</TableCell>
                    <TableCell className="text-white">{s.email}</TableCell>
                    <TableCell className="text-[#D4AF37] text-xs font-bold">{s.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Admin;
